import React, { useEffect } from "react";
import AttachedDocuments from "../AttachedDocuments";
import { useCompoundResourceApi } from "../../api/ResourceAPI";
import { UseMutationResult } from "react-query";
import { ThreeSixtyItemTypes } from "../../api/Items";
import { useGetItemDocuments } from "../../api/360FilesAPI";
import { PaperClipIcon } from "@heroicons/react/24/outline";
import { classNames, LONG_TEXT_WRAP_CLASSES } from "../../utils";

const newsAndWebFields = [
  {
    id: "title",
    label: "Title",
    type: "text",
    placeholder: "Enter title",
    required: true,
  },
  {
    id: "url",
    label: "URL",
    type: "url",
    placeholder: "Enter news URL",
    required: false,
  },
  {
    id: "author",
    label: "Author",
    type: "text",
    placeholder: "Enter author",
  },
  {
    id: "date",
    label: "Date",
    type: "date",
    placeholder: "Enter date",
    required: false,
  },
  {
    id: "body",
    label: "Body",
    type: "textarea",
    placeholder: "Enter body",
    required: true,
  },
  {
    id: "files",
    label: "Upload Files",
    type: "file",
    placeholder: "",
  },
];

export interface NewsItem {
  external_id: string;
  title: string;
  url?: string;
  author?: string;
  date?: string;
  body?: string;
  created_at?: string;
}

interface NewsProps {
  report_uuid: string;
  person_uuid: string;
  workflow_run_uuid: string;
}

const NewsRenderer: React.FC<{ doc: NewsItem }> = ({ doc }) => {
  const { data: uploadedFiles = [], refetch: refetchUploadedFiles } =
    useGetItemDocuments(doc.external_id, true);

  useEffect(() => {
    refetchUploadedFiles();
  }, [refetchUploadedFiles, doc.external_id]);

  return (
    <div className="min-w-0">
      <h3
        className={classNames(
          "text-gray-700 font-medium text-sm",
          LONG_TEXT_WRAP_CLASSES
        )}
      >
        {doc.title}
      </h3>
      {uploadedFiles.map((file: { filename: string; url: string }) => (
        <div
          key={file.filename}
          className="flex flex-row items-center gap-2 p-1 cursor-pointer min-w-0"
          onClick={() => window.open(file.url, "_blank")}
        >
          <span className="w-4 h-4 flex-shrink-0">
            <PaperClipIcon className="w-4 h-4" />
          </span>
          <p
            className={classNames(
              "text-gray-500 text-xs flex-1",
              LONG_TEXT_WRAP_CLASSES
            )}
          >
            {file.filename}
          </p>
        </div>
      ))}
      <p className="text-gray-500 text-xs">{doc.author}</p>
      {doc.date && <p className="text-gray-500 text-xs">{doc.date}</p>}
    </div>
  );
};

const News: React.FC<NewsProps> = ({
  report_uuid,
  person_uuid,
  workflow_run_uuid,
}) => {
  const {
    data: newsItems,
    isLoading,
    error,
    create,
    remove,
    createNoInvalidate,
    updateNoInvalidate,
    invalidateQuery,
  } = useCompoundResourceApi<NewsItem>(
    `itemdal/reports/${report_uuid}/news`,
    `itemdal/news`
  );

  async function handleExtracted(items: any[]) {
    if (!items.length) return;
    await Promise.all(items.map((item) => create.mutateAsync(item)));
  }

  return (
    <AttachedDocuments
      title="News and Web"
      fields={newsAndWebFields}
      items={newsItems}
      sortBy="date"
      isLoading={isLoading}
      error={error as string}
      report_uuid={report_uuid}
      createItemMutation={
        createNoInvalidate as UseMutationResult<any, Error, any>
      }
      updateItemMutation={
        updateNoInvalidate as UseMutationResult<any, Error, any>
      }
      removeItemMutation={remove as UseMutationResult<any, Error, any>}
      confirmRemove={true}
      person_uuid={person_uuid}
      item_type={ThreeSixtyItemTypes.NEWS}
      onExtractedFromFiles={handleExtracted}
      bgColorClass="bg-blue-400"
      DocRenderer={NewsRenderer}
      invalidateQuery={invalidateQuery}
      workflow_run_uuid={workflow_run_uuid}
    />
  );
};

export default News;
